import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchOneQuestionApi, updateQuestionApi } from "src/services/question";
import { getControls } from "src/services/controls";
import { fetchAllCategoryDataApi, fetchAllDataApi } from "src/services/riskServices";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { MdOutlineDelete } from "react-icons/md";
import { getUserRolePermissions } from "src/services/userServicer";

import { getReferences } from "src/services/reference";
import LoadingScreen from "../../../components/Loading/LoadingScreen";
interface QuestionFormValues {
  en_questionText: string;
  ar_questionText: string;
  type: string;
  category: string;
  risk: string[];
  control: string[];
  options: string[];
  reference: string[];
}


const UpdateQuestion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [controlsOptions, setControlsOptions] = useState<any[]>([]);
  const [riskOptions, setRiskOptions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [referenceOptions, setReferenceOptions] = useState<any[]>([]);
  const [question, setQuestion] = useState<QuestionFormValues>({
    en_questionText: '',
    ar_questionText: '',
    type: '',
    options: [],
    risk: [],
    control: [],
    category: '',
    reference: [],
  });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<QuestionFormValues>();


  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '36px',
    }),
  };

  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    const fetchUserRolePermissions = async () => {
      try {
        setLoading(true);

        
          
            //let userId:any = localStorage.getItem('userId');
            const permissions = await getUserRolePermissions();
            if(permissions.success){
              const compliancePagePermission:any = Object.values(permissions.data.permissions).find((permission: any) => permission.page === "survey");
              if(compliancePagePermission){
               
              setUserPermissions(compliancePagePermission?.permissions);
    
              if(compliancePagePermission?.permissions?.edit){

              
                 // Fetch Controls
          const controlsResponse: any = await getControls();
          if (controlsResponse?.data && controlsResponse?.data?.length > 0) {
            setControlsOptions(
              controlsResponse.data.map((control: any) => ({
                value: control.id,
                label: control.description,
              }))
            );
          }
  
          // Fetch Risks
          const risksResponse = await fetchAllDataApi();
          if (risksResponse?.data && risksResponse?.data?.length > 0) {
            setRiskOptions(
              risksResponse.data.map((risk: any) => ({
                value: risk.id,
                label: risk.name,
              }))
            );
          }
  
          // Fetch Categories
          const categoriesResponse = await fetchAllCategoryDataApi();
          if (categoriesResponse?.success) {
            setCategories(categoriesResponse.data);
          }

          const referencesResponse = await getReferences('', 500, 0);
          setReferenceOptions(referencesResponse?.data?.map((r: any) => ({
            value: r.id,
            label: r.name
          })) || []);
  
          if (id) {
            const response = await fetchOneQuestionApi(`bank-questions/${id}`);
            if (response?.success) {
              const questionData = response.data;
              const referenceIds = questionData.reference_id || []; // Fetch reference_id correctly as an array
  
              setQuestion({
                en_questionText: questionData.question_en || '',
                ar_questionText: questionData.question_ar || '',
                type: questionData.question_type || '',
                options: questionData.choices ? questionData.choices.split(",") : [],
                risk: questionData.risks.map((r: any) => r.risk_id),
                control: questionData.controls.map((c: any) => c.control_id),
                category: questionData.categoryId || '',
                reference: referenceIds,
              });
  
              setValue("en_questionText", questionData.question_en || '');
              setValue("ar_questionText", questionData.question_ar || '');
              setValue("type", questionData.question_type || '');
              setValue("options", questionData.choices ? questionData.choices.split(",") : []);
              setValue("risk", questionData.risks.map((r: any) => r.risk_id));
              setValue("control", questionData.controls.map((c: any) => c.control_id));
              setValue("category", questionData.categoryId || '');
              setValue("reference", referenceIds);
            } else {
              toast.error("Error fetching question");
            }
          }
                
                setNotAllowed(false);
                setLoading(false);
              } else {
                setNotAllowed(true);
                setLoading(false);
              }
            } else {
              toast.error(permissions.message);
              setLoading(false);
            }
    
          }
            
          
        ;
    
        

      } catch (error: any) {
        toast.error(t("permissions.Error_fetching_user_role_permissions")+":", error);
        setLoading(false);
      }
    }
      
    
      fetchUserRolePermissions(); 
  }, [id, setValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setQuestion((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

;

const handleReferenceChange = (selectedOptions: any) => {
  const selectedValues = selectedOptions ? selectedOptions?.map((opt: any) => opt.value) : [];
  setQuestion((prev) => ({
    ...prev,
    reference: selectedValues,
  }));
  setValue("reference", selectedValues); // Ensure react-hook-form is updated
};



  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...question.options];
    updatedOptions[index] = value;
    setQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const addOption = () => {
    setQuestion((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const removeOption = (index: number) => {
    const updatedOptions = question.options.filter((_, i) => i !== index);
    setQuestion((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const onSubmit = async (data: QuestionFormValues) => {
    try {
      if (!id) return;
  
      const payload = {
        en_questionText: question.en_questionText,
        ar_questionText: question.en_questionText,
        type: question.type,
        options: question.options.join(","), // Convert to a string if required
        category: question.category,
        risk: question.risk,
        control: question.control,
        referenceId: question.reference,
        
       
      };
  
      const response = await updateQuestionApi(id, payload);
      if (response?.success) {
        toast.success("Question updated successfully");
        navigate("/bankQuestions");
      } else {
        toast.error(response?.message || "Failed to update question");
      }
    } catch (error) {
      toast.error("An error occurred while updating the question");
    }
  };
  


  if (loading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }

  if (!loading) {

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem>{t("Questions")}</BreadcrumbItem>
        <BreadcrumbItem active>{t("Update Question")}</BreadcrumbItem>
      </Breadcrumb>
      <h5>{t("Update Question")}</h5>

      {notAllowed ? <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>:<form onSubmit={handleSubmit(onSubmit)} className="create-compliance-form">
        <div className="form-section2 mb-5">
          <h5 className="section-title">{t("BasicInfo")}</h5>

          <div className="form-group">
            <label>question</label>
            <input
              type="text"
              value={question.en_questionText}
              className="form-control"
              {...register("en_questionText", { required: true })}
              onChange={handleInputChange}
            />
            {errors.en_questionText && <span className="text-danger">{t('This field is required')}</span>}
          </div>

          {/*<div className="form-group">
            <label>question (Arabic)</label>
            <input
              type="text"
              value={question.ar_questionText}
              className="form-control"
              {...register("ar_questionText", { required: true })}
              onChange={handleInputChange}
            />
            {errors.ar_questionText && <span className="text-danger">{t('This field is required')}</span>}
          </div>*/}

          <div className="form-group">
            <label htmlFor="category">{t("Reference")}</label>
            <Select
              options={referenceOptions}
              value={referenceOptions?.find(option => option.value === question.reference)}
              onChange={handleReferenceChange}
              styles={customSelectStyles}
              isClearable
            /> 


          </div>


          <div className="form-group">
            <label htmlFor="category">{t("Category")}</label>
            <select
              id="category"
              style={{ height: "50px" }}
              className={`form-control ${errors.category ? "is-invalid" : ""}`}
              {...register("category", { required: t("Category is required") ?? "Category is required" })}
              onChange={handleInputChange}
            >
              <option value="">{t("Select Category")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category?.name}
                </option>
              ))}
            </select>
            {errors.category?.message && (
              <div className="invalid-feedback">
                {String(errors.category.message)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">{t("Type")}</label>
            <Select
              options={[
                { value: 'textarea', label: t("Text") },
                { value: 'multiple_choice', label: t("Multiple Choice") },
                { value: 'single_choice', label: t("Single Choice") },
                { value: 'compliance_related', label: t('Related to compliance') },
                { value: 'risk_related', label: t('Related to risk') }
              ]}
              className="basic-single"
              classNamePrefix="select"
              value={{ value: question.type, label: question.type }}
              onChange={(selectedOption: any) => {
                const value = selectedOption ? selectedOption.value : '';
                setQuestion((prev) => ({
                  ...prev,
                  type: value
                }));
              }}
            />
            {errors.type && <span className="text-danger">{t('This field is required')}</span>}
          </div>

          {(question.type === "multiple_choice" || question.type === "single_choice") && (
            <div className="form-group">
              <label className="form-label">{t("Options")}</label>
              <button
                type="button"
                onClick={addOption}
                className="btn btn-warning mr-2 ml-2"
              >
                + {t("Add Option")}
              </button>
              {question.options.map((option, index) => (
                <div key={index} className="d-flex flex-row mt-2">
                  <input
                    type="text"
                    defaultValue={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    className="form-control"
                    placeholder={`Option ${index + 1}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="action-button-danger"
                  >
                    <MdOutlineDelete size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>


    

       
        <div className="form-section2 mb-5" style={{ display: question.type === 'compliance_related' || question.type === 'risk_related' ? 'block' : 'none' }}>
          <h5 className="section-title">{t('relatedTo')}</h5>

          <div className="form-group" style={{ display: question.type === 'compliance_related' ? 'block' : 'none' }}>
            <label className="form-label">{t("Control")}</label>
            <Select
              options={controlsOptions}
              isMulti={true}
              className="basic-single"
              classNamePrefix="select"
              value={controlsOptions.filter(option => question.control.includes(option.value))}
              onChange={(selectedOptions: any) => {
                setQuestion((prev) => ({
                  ...prev,
                  control: selectedOptions ? selectedOptions?.map((option: any) => option.value) : []
                }));
              }}
            />
          </div>

          <div className="form-group" style={{ display: question.type === 'risk_related' ? 'block' : 'none' }}>
            <label className="form-label">{t("Risk")}</label>
            <Select
              options={riskOptions}
              isMulti={true}
              className="basic-single"
              classNamePrefix="select"
              value={riskOptions.filter(option => question.risk.includes(option.value))}
              onChange={(selectedOptions: any) => {
                setQuestion((prev) => ({
                  ...prev,
                  risk: selectedOptions ? selectedOptions?.map((option: any) => option.value) : []
                }));
              }}
            />
          </div>
        </div>

        <button type="submit" className="submit-button4" style={{marginBottom: '20px',marginTop: '10px'}}>
          {t("Update Question")}
        </button>
      </form>}
    </div>
  );
}
};

export default UpdateQuestion;






